import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MaskedLine, Overline, FadeUp, Stagger, StaggerItem } from "@/components/site/Reveal";
import { EditorialMarquee } from "@/components/site/Marquee";
import { postContact } from "@/lib/api";
import { PACKAGES, SPONSOR_MOMENTS, IMAGES } from "@/data/content";

const inputCls =
  "w-full border border-white/20 bg-black/40 px-5 py-4 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-brand-accent focus:ring-1 focus:ring-brand-accent";

const SponsorMoments = () => (
  <section className="border-t border-white/10 bg-ink-2" data-testid="sponsor-moments">
    <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
      <FadeUp className="max-w-3xl">
        <Overline>Recurring Moments</Overline>
        <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tighter sm:text-5xl" data-testid="sponsor-moments-heading">
          Own a piece of<br />the <span className="text-brand-accent">round.</span>
        </h2>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-muted">
          From a full episode to a piece of the round, Fore gives brands multiple ways to
          become part of the experience through authentic, integrated partnerships.
        </p>
      </FadeUp>
      <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-6">
        {SPONSOR_MOMENTS.map((m, i) => (
          <StaggerItem key={m.name} className={`lg:col-span-2 ${i === 3 ? "lg:col-start-2" : ""}`}>
            <article
              className="group flex h-full flex-col border border-white/10 bg-ink p-7 transition-colors duration-500 hover:border-brand"
              data-testid={`moment-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <h3 className="font-display text-2xl font-extrabold uppercase leading-tight tracking-tight text-white transition-colors duration-300 group-hover:text-brand-accent">
                {m.name}
              </h3>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-muted">{m.desc}</p>
            </article>
          </StaggerItem>
        ))}
      </Stagger>
      <FadeUp className="mt-12">
        <a
          href="#partner-form"
          data-testid="moments-cta"
          className="group inline-flex items-center gap-3 bg-white px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent"
        >
          Partner With Fore
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </FadeUp>
    </div>
  </section>
);

const SponsorForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business: "", title: "", description: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await postContact({
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.business,
        title: form.title,
        inquiry_type: "sponsorship",
        message: form.description,
      });
      toast.success("Inquiry sent — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", business: "", title: "", description: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="partner-form" className="scroll-mt-24 border-t border-white/10" data-testid="sponsor-form-section">
      <div className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          <FadeUp>
            <Overline>Start The Conversation</Overline>
            <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tighter sm:text-5xl" data-testid="sponsor-form-heading">
              Tell us about<br />your <span className="text-brand-accent">brand.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-muted">
              A few details and we'll come back with ideas, availability, and rates.
              Every inquiry lands directly in our inbox — no middlemen.
            </p>
          </FadeUp>

          <FadeUp delay={0.15}>
            <form onSubmit={submit} className="border border-white/10 bg-ink-2 p-7 md:p-10" data-testid="sponsor-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="sponsor-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Name *</label>
                  <input id="sponsor-name" data-testid="sponsor-form-name" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="sponsor-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Email *</label>
                  <input id="sponsor-email" data-testid="sponsor-form-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="sponsor-phone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Phone</label>
                  <input id="sponsor-phone" data-testid="sponsor-form-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="sponsor-business" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Business *</label>
                  <input id="sponsor-business" data-testid="sponsor-form-business" required value={form.business} onChange={set("business")} placeholder="Company or brand" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="sponsor-title" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Title</label>
                  <input id="sponsor-title" data-testid="sponsor-form-title" value={form.title} onChange={set("title")} placeholder="e.g. Marketing Director" className={inputCls} />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="sponsor-description" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Description *</label>
                <textarea
                  id="sponsor-description"
                  data-testid="sponsor-form-description"
                  required
                  rows={5}
                  value={form.description}
                  onChange={set("description")}
                  placeholder="Tell us about your brand and what kind of partnership you have in mind…"
                  className={`${inputCls} resize-none`}
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="sponsor-form-submit"
                className="group mt-8 flex w-full items-center justify-center gap-3 bg-brand-accent px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-white disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                {sending ? "Sending…" : "Send Inquiry"}
              </button>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

export default function Sponsor() {
  return (
    <main data-testid="sponsor-page">
      <section className="relative overflow-hidden">
        <img src={IMAGES.podcast} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25 contrast-125 saturate-[0.7]" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-36 md:px-8 md:pb-24 md:pt-48">
          <MaskedLine>
            <Overline>Partnerships</Overline>
          </MaskedLine>
          <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-7xl lg:text-8xl" data-testid="sponsor-heading">
            <MaskedLine delay={0.15}>Sponsor</MaskedLine>
            <MaskedLine delay={0.3}>with <span className="text-brand-accent">us.</span></MaskedLine>
          </h1>
          <FadeUp delay={0.5}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/80 md:text-lg">
              A golf audience that skews founder, executive, and competitor. Integrations that feel
              like part of the show — not an interruption.
            </p>
            <a
              href="#packages"
              data-testid="sponsor-jump-to-packages"
              className="group mt-8 inline-flex items-center gap-3 border-b border-brand-accent pb-1 font-mono text-xs uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:text-brand-accent"
            >
              See Sponsorship Options
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </FadeUp>
        </div>
      </section>

      <section id="packages" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-20 md:px-8 md:py-28" data-testid="sponsor-packages">
        <FadeUp>
          <Overline>Sponsorship Opportunities</Overline>
          <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tighter sm:text-5xl" data-testid="sponsor-packages-heading">
            Pick your<br />starting tee.
          </h2>
        </FadeUp>
        <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-6">
          {PACKAGES.map((p, i) => (
            <StaggerItem key={p.name} className={`lg:col-span-2 ${i === 3 ? "lg:col-start-2" : ""}`}>
              <article
                className={`flex h-full flex-col border p-8 transition-colors duration-500 ${
                  p.featured ? "border-brand-accent bg-ink-2 drop-shadow-[0_4px_24px_rgba(204,255,0,0.08)]" : "border-white/10 bg-ink-2 hover:border-brand"
                }`}
                data-testid={`package-${p.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                {p.featured && (
                  <span className="mb-6 w-fit bg-brand-accent px-3 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-ink">
                    Premier Option
                  </span>
                )}
                <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight">{p.name}</h3>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.25em] text-brand-accent">{p.price}</p>
                <ul className="mt-8 flex flex-1 flex-col gap-4">
                  {p.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-muted">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-accent" strokeWidth={3} />
                      {item}
                    </li>
                  ))}
                </ul>
                <a
                  href="#partner-form"
                  data-testid={`package-cta-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                  className={`group mt-10 flex items-center justify-center gap-3 px-6 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                    p.featured ? "bg-brand-accent text-ink hover:bg-white" : "border border-white/25 text-white hover:border-brand-accent hover:text-brand-accent"
                  }`}
                >
                  Start The Conversation
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </article>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <SponsorMoments />

      <SponsorForm />

      <EditorialMarquee />

      <section className="border-y border-white/10 bg-ink-2" data-testid="sponsor-coming-soon">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <FadeUp>
            <div className="grid place-items-center border border-dashed border-white/15 px-6 py-20 text-center md:py-28">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-brand-accent">Founding Partners</p>
                <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[0.9] tracking-tighter sm:text-6xl" data-testid="sponsors-coming-soon-heading">
                  Sponsors<br />Coming Soon
                </h2>
                <p className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-muted md:text-base">
                  We're opening a small number of founding sponsor spots for the launch season.
                  Get in early and shape the partnership from day one.
                </p>
                <Link
                  to="/contact?type=sponsorship"
                  data-testid="sponsors-coming-soon-cta"
                  className="group mt-10 inline-flex items-center gap-3 bg-brand-accent px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-white"
                >
                  Become a Founding Sponsor
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
